import resolveResponse from 'contentful-resolve-response'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ThemeProvider} from '@primer/react-brand'

import {cohortFunnelBuilder} from '../../../lib/analytics'
import {ZodSilentErrorBoundary} from '../../../components/ZodSilentErrorBoundary/ZodSilentErrorBoundary'
import {toPayload} from '../../../lib/types/payload'
import {toBrandPage} from '../../../brand/lib/types/contentful'
import type {GenericGroup, GenericSectionWithIds} from '../../../brand/lib/types/contentful'

import {FaqSection} from './_sections/FaqSection/FaqSection'
import {FeaturesSection} from './_sections/FeaturesSection/FeaturesSection'
import {FootnotesSection} from './_sections/FootnotesSection/FootnotesSection'
import {HeroSection} from './_sections/HeroSection/HeroSection'
import {IntegrationsSection} from './_sections/IntegrationsSection/IntegrationsSection'
import {PricingSection} from './_sections/PricingSection/PricingSection'
import {ResourcesSection} from './_sections/ResourcesSection/ResourcesSection'

import {PlanTypeContextProvider} from './_context/PlanTypeContext'

export default function FeaturesCopilotIndex() {
  const {contentfulRawJsonResponse} = toPayload(useRoutePayload<unknown>())
  const contentfulResponse = resolveResponse(contentfulRawJsonResponse)
  const page = toBrandPage(contentfulResponse)

  const {template} = page.fields
  const {subnav} = template.fields
  const {
    featuresCopilotFaqSection,
    featuresCopilotFeaturesSection,
    featuresCopilotHeroSection,
    featuresCopilotIntegrationsSection,
    featuresCopilotResourcesSection,
  } = page.ids as {
    featuresCopilotFaqSection: GenericSectionWithIds
    featuresCopilotFeaturesSection: GenericSectionWithIds
    featuresCopilotHeroSection: GenericSectionWithIds
    featuresCopilotIntegrationsSection: GenericSectionWithIds
    featuresCopilotResourcesSection: GenericSectionWithIds
  }

  const {featuresCopilotFootnotes} = featuresCopilotFaqSection?.ids as {
    featuresCopilotFootnotes: GenericGroup
  }

  let {copilotBusinessContactSalesPath} = useRoutePayload<{copilotBusinessContactSalesPath: string}>()
  let {copilotBusinessSignupPath} = useRoutePayload<{copilotBusinessSignupPath: string}>()
  let {copilotEnterpriseContactSalesPath} = useRoutePayload<{copilotEnterpriseContactSalesPath: string}>()
  let {copilotEnterpriseSignupPath} = useRoutePayload<{copilotEnterpriseSignupPath: string}>()
  let {copilotProPlusSignupPath} = useRoutePayload<{copilotProPlusSignupPath: string}>()
  let {copilotProSignupPath} = useRoutePayload<{copilotProSignupPath: string}>()
  const {cft} = useRoutePayload<{cft: string}>()

  const withCft = cohortFunnelBuilder(cft)
  copilotBusinessContactSalesPath = withCft(copilotBusinessContactSalesPath)
  copilotBusinessSignupPath = withCft(copilotBusinessSignupPath, {product: 'cfb'})
  copilotEnterpriseContactSalesPath = withCft(copilotEnterpriseContactSalesPath)
  copilotEnterpriseSignupPath = withCft(copilotEnterpriseSignupPath, {product: 'ce'})
  copilotProSignupPath = withCft(copilotProSignupPath, {product: 'cfi'})
  copilotProPlusSignupPath = withCft(copilotProPlusSignupPath, {product: 'cpp'})

  return (
    <ZodSilentErrorBoundary>
      <PlanTypeContextProvider>
        <ThemeProvider colorMode="dark" className="lp-Copilot">
          {featuresCopilotHeroSection ? (
            <HeroSection contentfulContent={featuresCopilotHeroSection} subnav={subnav} />
          ) : null}

          {featuresCopilotFeaturesSection ? (
            <FeaturesSection contentfulContent={featuresCopilotFeaturesSection} />
          ) : null}

          <PricingSection
            copilotBusinessContactSalesPath={copilotBusinessContactSalesPath}
            copilotBusinessSignupPath={copilotBusinessSignupPath}
            copilotEnterpriseContactSalesPath={copilotEnterpriseContactSalesPath}
            copilotEnterpriseSignupPath={copilotEnterpriseSignupPath}
            copilotProPlusSignupPath={copilotProPlusSignupPath}
            copilotProSignupPath={copilotProSignupPath}
          />

          {featuresCopilotIntegrationsSection ? (
            <IntegrationsSection contentfulContent={featuresCopilotIntegrationsSection} />
          ) : null}

          {featuresCopilotResourcesSection ? (
            <ResourcesSection contentfulContent={featuresCopilotResourcesSection} />
          ) : null}

          {featuresCopilotFaqSection ? <FaqSection contentfulContent={featuresCopilotFaqSection} /> : null}

          {featuresCopilotFootnotes ? <FootnotesSection contentfulContent={featuresCopilotFootnotes} /> : null}
        </ThemeProvider>
      </PlanTypeContextProvider>
    </ZodSilentErrorBoundary>
  )
}

try{ FeaturesCopilotIndex.displayName ||= 'FeaturesCopilotIndex' } catch {}